import type { OpportunityKind } from './types';

export type CampaignChannel = 'whatsapp' | 'email';

export const CHANNEL_LABEL: Record<CampaignChannel, string> = {
  whatsapp: 'WhatsApp',
  email: 'Email',
};

interface TemplateBag {
  // Pieces that get assembled together. Each generation picks one of each.
  openers: string[];
  hooks: string[];
  ctas: string[];
  // Email-specific signature line (omitted on WhatsApp).
  closers: string[];
}

const SIGNATURE = '— Equipo médico de Dr. Andrés Vásquez · OptimalPraxis';

// Templates curated per (kind × channel). Variables are referenced via {nombre}
// so the user can preview merge tags. Replace with a real LLM call when the
// generation backend is available — see generateMessage below for the seam.
const T: Record<OpportunityKind, Record<CampaignChannel, TemplateBag>> = {
  'no-followup': {
    whatsapp: {
      openers: [
        'Hola {nombre}, te escribe el equipo de Dr. Andrés.',
        'Hola {nombre}, espero estés bien.',
      ],
      hooks: [
        'Revisamos tu último examen y hay un par de marcadores que vale la pena conversar en consulta.',
        'Tus últimos resultados muestran cambios que el doctor quiere comentarte personalmente.',
      ],
      ctas: [
        '¿Te acomoda agendar una consulta de seguimiento esta semana?',
        '¿Coordinamos un horario para verte pronto?',
      ],
      closers: [],
    },
    email: {
      openers: [
        'Estimado/a {nombre}, le escribimos desde el equipo médico de Dr. Andrés Vásquez.',
      ],
      hooks: [
        'Tras revisar su última evaluación, identificamos hallazgos que requieren una consulta de seguimiento.',
      ],
      ctas: [
        'Le pedimos coordinar un horario en los próximos días respondiendo a este correo o por WhatsApp.',
      ],
      closers: [SIGNATURE],
    },
  },
  'annual-checkup': {
    whatsapp: {
      openers: [
        'Hola {nombre}, ¿cómo estás?',
        'Hola {nombre}, te saluda el equipo de Dr. Andrés.',
      ],
      hooks: [
        'Notamos que ya pasó casi un año desde tu última evaluación.',
        'Estamos por cumplir 12 meses desde tu último chequeo y queríamos recordarte.',
      ],
      ctas: [
        '¿Te coordinamos un espacio para tu chequeo anual?',
        'Si te queda fácil, te agendamos esta semana o la próxima — tú decides.',
      ],
      closers: [],
    },
    email: {
      openers: ['Estimado/a {nombre},'],
      hooks: [
        'Le recordamos que su última evaluación clínica integral fue hace aproximadamente un año.',
      ],
      ctas: [
        'Le invitamos a programar su chequeo anual respondiendo a este mensaje o por nuestro WhatsApp clínico.',
      ],
      closers: [SIGNATURE],
    },
  },
  'metabolic-risk': {
    whatsapp: {
      openers: ['Hola {nombre}, te escribe el equipo de Dr. Andrés.'],
      hooks: [
        'Al revisar tu evaluación más reciente, vimos algunos marcadores metabólicos en zona de atención (glucosa, triglicéridos).',
        'Notamos cambios en tu perfil metabólico que conviene trabajar a tiempo.',
      ],
      ctas: [
        'El doctor quiere proponerte un plan de 8 semanas para mejorarlos. ¿Te coordino un horario para conversarlo?',
        '¿Te agendamos para revisar opciones de plan junto al doctor?',
      ],
      closers: [],
    },
    email: {
      openers: ['Estimado/a {nombre},'],
      hooks: [
        'En su última evaluación se identificaron señales metabólicas que requieren acompañamiento.',
      ],
      ctas: [
        'Sugerimos coordinar una consulta para diseñar un plan de seguimiento personalizado.',
      ],
      closers: [SIGNATURE],
    },
  },
  'cardiovascular-risk': {
    whatsapp: {
      openers: ['Hola {nombre}, te escribe el equipo de Dr. Andrés.'],
      hooks: [
        'En tu última evaluación detectamos indicadores cardiovasculares que conviene revisar (perfil lipídico, presión).',
        'Tus resultados recientes muestran señales cardiovasculares que el doctor quiere conversar contigo.',
      ],
      ctas: [
        '¿Agendamos una consulta para verlos en detalle y definir el siguiente paso?',
        '¿Te coordino un horario esta semana para una evaluación cardiovascular?',
      ],
      closers: [],
    },
    email: {
      openers: ['Estimado/a {nombre},'],
      hooks: [
        'Su última evaluación muestra indicadores cardiovasculares que recomendamos revisar en consulta presencial.',
      ],
      ctas: [
        'Por favor coordine una cita lo antes posible respondiendo este correo o por WhatsApp.',
      ],
      closers: [SIGNATURE],
    },
  },
  inactive: {
    whatsapp: {
      openers: [
        'Hola {nombre}, hace tiempo que no nos vemos por acá.',
        'Hola {nombre}, ¿cómo has estado?',
      ],
      hooks: [
        'Queríamos saber cómo va tu salud y si te gustaría retomar tu seguimiento con nosotros.',
        'Nos gustaría retomar contacto y, si quieres, ayudarte a planificar tu próxima evaluación.',
      ],
      ctas: [
        '¿Te interesa que te reservemos un horario?',
        'Si quieres, te coordinamos una consulta breve para revisar dónde estás hoy.',
      ],
      closers: [],
    },
    email: {
      openers: ['Estimado/a {nombre},'],
      hooks: [
        'Hace más de un año desde su última actividad con nosotros y nos gustaría retomar el seguimiento de su salud.',
      ],
      ctas: [
        'Si lo desea, podemos agendar una consulta de reenganche en el horario que mejor le acomode.',
      ],
      closers: [SIGNATURE],
    },
  },
};

export interface PromptHints {
  brief?: boolean;
  warm?: boolean;
  scheduleFocus?: boolean;
  reassuring?: boolean;
}

export function detectHints(prompt: string): PromptHints {
  const p = prompt.toLowerCase();
  return {
    brief: /breve|corto|conciso|simple/.test(p),
    warm: /cálid|cercan|amable|human/.test(p),
    scheduleFocus: /agend|cita|consulta|horario|reserv/.test(p),
    reassuring: /sin alarm|tranquil|reasegur|amig|relajad/.test(p),
  };
}

export interface GeneratedMessage {
  subject?: string;
  body: string;
}

// Deterministic-ish pick so "regenerate" cycles through variants instead
// of repeating the same one.
function pick<T>(arr: T[], seed: number): T {
  if (arr.length === 0) return '' as unknown as T;
  return arr[seed % arr.length];
}

const SUBJECTS: Record<OpportunityKind, string> = {
  'no-followup': 'Seguimiento de tu última evaluación',
  'annual-checkup': 'Es momento de tu chequeo anual',
  'metabolic-risk': 'Tu perfil metabólico — recomendaciones',
  'cardiovascular-risk': 'Resultados cardiovasculares — siguiente paso',
  inactive: 'Retomemos tu seguimiento',
};

export function generateMessage(
  kind: OpportunityKind,
  channel: CampaignChannel,
  prompt: string,
  seed: number
): GeneratedMessage {
  const bag = T[kind][channel];
  const hints = detectHints(prompt);

  const opener = pick(bag.openers, seed);
  const hook = pick(bag.hooks, seed + 1);
  const cta = pick(
    hints.scheduleFocus
      ? bag.ctas.filter((c) => /agend|coord|horario|reserv/.test(c.toLowerCase()))
      : bag.ctas,
    seed + 2
  ) || pick(bag.ctas, seed + 2);

  const parts: string[] = [];
  parts.push(opener);
  if (!hints.brief) parts.push(hook);
  parts.push(cta);

  const closer = bag.closers[0];
  const body =
    channel === 'email'
      ? `${parts.join('\n\n')}${closer ? `\n\n${closer}` : ''}`
      : parts.join(' ');

  return {
    body,
    subject: channel === 'email' ? SUBJECTS[kind] : undefined,
  };
}

export const PROMPT_HINT_CHIPS: { label: string; value: string }[] = [
  { label: 'Tono cálido', value: 'tono cálido y cercano' },
  { label: 'Más breve', value: 'más breve, ir al grano' },
  { label: 'Enfocado en agendar', value: 'enfocado en agendar consulta' },
  { label: 'Sin alarmar', value: 'sin alarmar al paciente' },
];
